import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

// Connects to data-controller="active-storage-upload"
export default class extends Controller {
  static targets = ["input", "progress", "error", "success", "dropzone", "dropping"]
  static values = { 
    url: String,
    acceptedTypes: { type: String, default: "application/pdf" }
  }

  connect() {
    this.setupDragAndDrop()
    this.setupFileInput()
  }

  setupDragAndDrop() {
    if (!this.hasDropzoneTarget) return

    const dropzone = this.dropzoneTarget

    // Prevent default drag behaviors
    ;['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      dropzone.addEventListener(eventName, this.preventDefaults, false)
      document.body.addEventListener(eventName, this.preventDefaults, false)
    })


    this.boundHighlight = this.highlight.bind(this)

    // Highlight drop zone when item is dragged over it
    ;['dragenter', 'dragover'].forEach(eventName => {
      dropzone.addEventListener(eventName, this.boundHighlight, false)
    })

    ;['dragleave', 'drop'].forEach(eventName => {
      dropzone.addEventListener(eventName, this.unhighlight, false)
    })

    // Handle dropped files
    dropzone.addEventListener('drop', this.handleDrop.bind(this), false)
  }

  setupFileInput() {
    if (!this.hasInputTarget) return

    this.inputTarget.addEventListener('change', this.handleFileSelect.bind(this))
  }

  preventDefaults(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  highlight(e) {
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.add('hidden')
    }
    if (this.hasDroppingTarget && this.droppingTarget.classList.contains('hidden')) {
      this.droppingTarget.classList.remove('hidden')
    }
  }

  unhighlight(e) {
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.remove('hidden')
    }
    if (this.hasDroppingTarget) {
      this.droppingTarget.classList.add('hidden')
    }
  }

  handleDrop(e) {
    const dt = e.dataTransfer
    const files = dt.files
    this.handleFiles(files)
    
    // Hide dropping target after drop
    if (this.hasDroppingTarget) {
      this.progressTarget.classList.remove('hidden')
      this.droppingTarget.classList.add('hidden')
    }
  }

  handleFileSelect(e) {
    const files = e.target.files
    this.handleFiles(files)
  }

  handleFiles(files) {
    if (files.length === 0) return

    const file = files[0]

    // Validate file type
    if (!this.isValidFileType(file)) {
      this.showError("This file type isn't supported. Please upload a PDF.")
      return
    }

    this.uploadFile(file)
  }

  isValidFileType(file) {
    const acceptedTypes = this.acceptedTypesValue.split(',').map(type => type.trim())
    return acceptedTypes.some(type => {
      if (type.startsWith('.')) {
        return file.name.toLowerCase().endsWith(type.toLowerCase())
      } else {
        return file.type.match(new RegExp(type.replace('*', '.*')))
      }
    })
  }

  uploadFile(file) {
    this.showProgress()

    const upload = new DirectUpload(file, this.urlValue, this)

    upload.create((error, blob) => {
      if (error) {
        this.showError("Upload failed. Please try again.")
        console.error('Upload error:', error)
      } else {
        this.showSuccess()
        this.createJobFileRecord(blob, file)
      }
    })
  }

  createJobFileRecord(blob, file) {
    // Create a hidden input with the blob signed ID
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = 'job[job_files_attributes][0][file]'
    input.value = blob.signed_id

    // Add to form
    const form = this.element.closest('form')
    if (form) {
      form.appendChild(input)

      // Trigger form submission
      const event = new CustomEvent('upload:done', {
        bubbles: true,
        detail: {
          blob: blob,
          file: file
        }
      })
      document.dispatchEvent(event)
    }
  }

  showProgress() {
    if (this.hasProgressTarget) {
      this.progressTarget.classList.remove('hidden')
      this.progressTarget.textContent = "Just a moment while the BiddyAI™ does its thing"
    }

    if (this.hasErrorTarget) {
      this.errorTarget.classList.add('hidden')
    }

    if (this.hasSuccessTarget) {
      this.successTarget.classList.add('hidden')
    }
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.classList.remove('hidden')
    }

    if (this.hasProgressTarget) {
      this.progressTarget.classList.add('hidden')
    }
  }

  showSuccess() {
    if (this.hasSuccessTarget) {
      this.successTarget.classList.remove('hidden')
    }

    if (this.hasProgressTarget) {
      this.progressTarget.classList.add('hidden')
    }
  }

  // DirectUpload delegate methods
  directUploadWillCreateBlobWithXHR(xhr) {
    xhr.upload.addEventListener("progress", event => {
      const progress = event.loaded / event.total * 100
      if (this.hasProgressTarget) {
        this.progressTarget.textContent = `Uploading ${Math.round(progress)}%`
      }
    })
  }

  directUploadWillStoreFileWithXHR(xhr) {
    // Optional: Add any additional headers or configuration
  }
} 